import React, { useEffect } from 'react';
import { FileText, Palette, Check, X } from 'lucide-react';

/* ================= compact modal shell (settings stay in admin's view) ================= */
export function AdminModal({ title, icon, desc, onClose, children, footer, wide }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div onClick={onClose} role="dialog" aria-modal="true" className="sheet-overlay"
      style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(12,10,9,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, direction: 'rtl', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} className="sheet-card"
        style={{ background: 'var(--card)', color: 'var(--foreground)', border: '1px solid var(--border)', borderRadius: 28, width: '100%', maxWidth: wide ? 680 : 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.25)', margin: 'auto' }}>
        <div style={{ padding: '16px 22px 14px', borderBottom: '1px solid var(--border)', background: 'var(--muted)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {icon && <span style={{ fontSize: '1.3rem' }}>{icon}</span>}
            <span style={{ fontWeight: 800, fontSize: '0.95rem', color: 'var(--foreground)', flex: 1 }}>{title}</span>
            <button type="button" onClick={onClose} aria-label="بستن"
              style={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--muted-foreground)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><X size={15} /></button>
          </div>
          {desc && <p style={{ fontSize: '0.74rem', color: 'var(--muted-foreground)', margin: '6px 0 0' }}>{desc}</p>}
        </div>
        <div style={{ overflowY: 'auto', padding: '18px 22px', flex: 1 }}>
          {children}
        </div>
        <div style={{ padding: '12px 22px', borderTop: '1px solid var(--border)', background: 'var(--muted)', flexShrink: 0, display: 'flex', gap: 10, alignItems: 'center' }}>
          {footer || (
            <>
              <span style={{ flex: 1, fontSize: '0.72rem', color: 'var(--muted-foreground)' }}>تغییرات خودکار ذخیره می‌شوند؛ «انجام شد» فقط پنجره را می‌بندد.</span>
              <button type="button" onClick={onClose}
                style={{ background: 'var(--brand)', color: 'var(--brand-foreground)', border: 'none', padding: '10px 28px', borderRadius: 999, fontSize: '0.85rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <Check size={15} /> انجام شد
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ================= widget settings: content tab + style tab =================
 * Opened from the content/style buttons in the widget card header. Short,
 * focused, always fully visible (internal scroll + sticky footer). */
export function WidgetSettingsModal({ icon, title, desc, tab, setTab, styleActive, onClose, content, style }) {
  const tabs = [
    ['content', <FileText size={16} />, 'محتوا'],
    ['style', <Palette size={16} />, 'استایل'],
  ];
  return (
    <AdminModal title={title} icon={icon} desc={desc} onClose={onClose} wide
      footer={null}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, position: 'sticky', top: -18, background: 'var(--card)', paddingTop: 2, paddingBottom: 8, zIndex: 2 }}>
        {tabs.map(([k, ic, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '11px', borderRadius: 999, border: tab === k ? 'none' : '1px solid var(--border)', background: tab === k ? 'var(--brand)' : 'transparent', color: tab === k ? 'var(--brand-foreground)' : 'var(--muted-foreground)', fontSize: '0.84rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
            <span>{ic}</span> {label}
            {k === 'style' && styleActive && <span style={{ fontSize: '0.66rem', fontWeight: 700, background: tab === k ? 'rgba(0,0,0,0.18)' : 'var(--brand)', color: tab === k ? 'var(--brand-foreground)' : 'var(--brand-foreground)', borderRadius: 20, padding: '2px 9px' }}>فعال</span>}
          </button>
        ))}
      </div>
      {tab === 'content' ? content : style}
    </AdminModal>
  );
}

/* ================= overflow (⋯) menu for card actions ================= */
export function CardMenu({ open, onClose, items }) {
  useEffect(() => {
    if (!open) return;
    const close = () => onClose();
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div onClick={e => e.stopPropagation()}
      style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 50, background: '#fff', border: '1px solid #E4E4E7', borderRadius: 12, boxShadow: '0 12px 32px rgba(0,0,0,0.14)', padding: 6, minWidth: 170, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {items.map((it, i) => (
        <button key={i} type="button" onClick={() => { onClose(); it.onClick(); }}
          style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'right', background: 'none', border: 'none', padding: '9px 12px', borderRadius: 8, fontSize: '0.8rem', fontWeight: 600, color: it.danger ? '#991B1B' : '#3F3F46', cursor: 'pointer', fontFamily: 'inherit' }}
          onMouseEnter={e => { e.currentTarget.style.background = it.danger ? '#FEF2F2' : '#F4F4F5'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
          <span style={{ fontSize: '0.95rem' }}>{it.icon}</span> {it.label}
        </button>
      ))}
    </div>
  );
}
